import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { useForm } from "@tanstack/react-form";
import React, { useEffect, useState } from "react";
import Form from "src/components/base/form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button } from "src/components/base/button";
import { EllipsisVerticalIcon, LinkIcon, PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Text } from "src/components/base/text";
import AddExtraDomainDialog from "src/components/dialogs/add-extra-domain";
import { FullExtraDnsDomain, FullExtraDnsRecord } from "src/api/generated/frontend_internal_v1";
import DeleteExtraDomainDialog from "src/components/dialogs/delete-extra-domain";
import UpdateExtraDomainDialog from "src/components/dialogs/update-extra-domain";
import DeleteExtraRecordDialog from "src/components/dialogs/delete-extra-record";
import AddExtraRecordDialog from "src/components/dialogs/add-extra-record";
import UpdateExtraRecordDialog from "src/components/dialogs/update-extra-record";
import { Subheading } from "src/components/base/heading";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from "src/components/base/dropdown";
import { Badge, BadgeButton } from "src/components/base/badges/base";

/**
 * The properties for {@link ExtraRecordsDnsOverview}
 */
export type ExtraRecordsDnsOverviewProps = {};

/**
 * Settings for extra dns records
 */
export default function ExtraRecordsDnsOverview(props: ExtraRecordsDnsOverviewProps) {
    const [t] = useTranslation("admin-dns-extra-records");
    const [openAddExtraDomain, setOpenAddExtraDomain] = React.useState(false);
    const [openDeleteExtraDomain, setDeleteExtraDomain] = React.useState<FullExtraDnsDomain>();
    const [openUpdateExtraDomain, setUpdateExtraDomain] = React.useState<FullExtraDnsDomain>();
    const [openAddExtraRecord, setOpenExtraRecord] = React.useState<FullExtraDnsDomain>();
    const [openDeleteExtraRecord, setDeleteExtraRecord] = React.useState<FullExtraDnsRecord>();
    const [openUpdateExtraRecord, setUpdateExtraRecord] = React.useState<FullExtraDnsRecord>();

    const navigate = useNavigate();
    const router = useRouter();

    const { search } = Route.useSearch();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    return (
        <div className={"flex flex-col gap-8"}>
            <div className={"flex flex-col gap-1"}>
                <Subheading>{t("heading.extra-records")}</Subheading>
                <Text>{t("description.extra-records")}</Text>
            </div>
            <div className={"flex flex-row justify-between"}>
                <Form className={"w-md"} onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field
                                name={"search"}
                                validators={{
                                    onChangeAsync: async ({ fieldApi }) => {
                                        await navigate({
                                            to: "/a/dns/extra-records",
                                            search: { search: fieldApi.state.value },
                                        });
                                    },
                                    onChangeAsyncDebounceMs: 500,
                                }}
                            >
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.search")}</Label>
                                        <Input
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </Form>
                <Button color={"blue"} onClick={() => setOpenAddExtraDomain(true)} className={"self-end"}>
                    <PlusIcon />
                    {t("button.add-extra-domain")}
                </Button>
            </div>
            <div className={"flex flex-col gap-16"}>
                {data.list
                    .sort((a, b) => a.from_dns.localeCompare(b.from_dns, undefined, { sensitivity: "base" }))
                    .map((domain) => (
                        <div className={"grid grid-cols-[1fr_2.5fr] gap-8"} key={domain.uuid}>
                            <div className={"flex flex-col gap-2"}>
                                <div className={"flex flex-row items-center gap-4"}>
                                    <Subheading>{domain.from_dns}</Subheading>{" "}
                                    <Dropdown>
                                        <DropdownButton className={"h-min"} plain={true}>
                                            <EllipsisVerticalIcon />
                                            <DropdownMenu anchor={"bottom end"}>
                                                <DropdownItem onClick={() => setUpdateExtraDomain(domain)}>
                                                    <PencilSquareIcon />

                                                    {t("button.update-extra-dns")}
                                                </DropdownItem>
                                                <DropdownItem onClick={() => setDeleteExtraDomain(domain)}>
                                                    <TrashIcon />

                                                    {t("button.delete-extra-dns")}
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </DropdownButton>
                                    </Dropdown>
                                </div>

                                <Text>{domain.description}</Text>
                            </div>

                            <div className={"flex flex-col gap-2"}>
                                {domain.records.length > 0 && (
                                    <Table dense={true} className={""}>
                                        <TableHead>
                                            <TableRow>
                                                <TableHeader>{t("label.record-type")}</TableHeader>
                                                <TableHeader>{t("label.address")}</TableHeader>
                                                <TableHeader className={"w-0"}></TableHeader>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {domain.records
                                                .sort((r1, r2) => {
                                                    if (r1.record_type !== r2.record_type) {
                                                        return r1.record_type.localeCompare(r2.record_type);
                                                    }
                                                    return r1.to_ip.localeCompare(r2.to_ip, undefined, {
                                                        numeric: true,
                                                    });
                                                })
                                                .map((record) => (
                                                    <TableRow key={record.uuid}>
                                                        <TableCell>
                                                            <Badge color={"blue"}>
                                                                <span> {record.record_type}</span>
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <ExtraDnsRecordAddress
                                                                device_uuid={record.device_uuid}
                                                                ip={record.to_ip}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className={"flex flex-row gap-2"}>
                                                                <Button
                                                                    plain={true}
                                                                    title={t("button.update-extra-record")}
                                                                    onClick={() => setUpdateExtraRecord(record)}
                                                                >
                                                                    <PencilSquareIcon />
                                                                </Button>
                                                                <Button
                                                                    plain={true}
                                                                    title={t("button.delete-extra-record")}
                                                                    onClick={() => setDeleteExtraRecord(record)}
                                                                >
                                                                    <TrashIcon />
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                )}
                                <Button outline={true} onClick={() => setOpenExtraRecord(domain)}>
                                    <PlusIcon />
                                    {t("button.add-extra-record")}
                                </Button>
                            </div>
                        </div>
                    ))}
            </div>

            {openAddExtraDomain && (
                <AddExtraDomainDialog
                    onClose={() => setOpenAddExtraDomain(false)}
                    onSubmit={async () => {
                        setOpenAddExtraDomain(false);
                        await router.invalidate({ sync: true });
                    }}
                />
            )}
            {openUpdateExtraDomain && (
                <UpdateExtraDomainDialog
                    dns={openUpdateExtraDomain}
                    onClose={() => setUpdateExtraDomain(undefined)}
                    onSubmit={async () => {
                        setUpdateExtraDomain(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
            )}
            {openDeleteExtraDomain && (
                <DeleteExtraDomainDialog
                    dns={openDeleteExtraDomain.uuid}
                    onClose={() => setDeleteExtraDomain(undefined)}
                    onDelete={async () => {
                        setDeleteExtraDomain(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
            )}
            {openDeleteExtraRecord && (
                <DeleteExtraRecordDialog
                    record={openDeleteExtraRecord.uuid}
                    onClose={() => setDeleteExtraRecord(undefined)}
                    onDelete={async () => {
                        setDeleteExtraRecord(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
            )}
            {openAddExtraRecord && (
                <AddExtraRecordDialog
                    dns={openAddExtraRecord}
                    onClose={() => setOpenExtraRecord(undefined)}
                    onSubmit={async () => {
                        setOpenExtraRecord(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
            )}
            {openUpdateExtraRecord && (
                <UpdateExtraRecordDialog
                    record={openUpdateExtraRecord}
                    onClose={() => setUpdateExtraRecord(undefined)}
                    onSubmit={async () => {
                        setUpdateExtraRecord(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />
            )}
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type ExtraRecordsSearch = {
    /** The search term that should be used */
    search: string;
};
export const Route = createFileRoute("/_admin/a/dns/_dns/extra-records")({
    component: ExtraRecordsDnsOverview,
    validateSearch: (search: Record<string, unknown>): ExtraRecordsSearch => {
        return { search: (search.search as string) || "" };
    },
    loaderDeps: ({ search: { search } }) => ({ search }),
    loader: async ({ deps }) =>
        await ApiInternal.dns.getAllExtraDns({
            filter_dns: deps.search,
        }),
});

/**
 * Props for ExtraDnsRecordAddress
 */
type ExtraDnsRecordAddressProps = {
    /** UUID of the linked device or null-ish if none is linked */
    device_uuid: string | null | undefined;
    /** IP address to display */
    ip: string;
};

/**
 * React component that renders an extra DNS record address along with its linked data.
 *
 * @param props React props.
 */
function ExtraDnsRecordAddress(props: ExtraDnsRecordAddressProps) {
    const { device_uuid, ip } = props;
    const [name, setName] = useState<string>("...");

    useEffect(() => {
        if (device_uuid) ApiInternal.devices.getSingle(device_uuid).then((d) => setName(d.dnsname));
        else setName("...");
    }, [device_uuid]);

    return device_uuid ? (
        <div className={"flex flex-row items-center gap-2"}>
            {ip}
            <BadgeButton color={"zinc"} href={"/a/devices/$deviceId/general"} params={{ deviceId: device_uuid }}>
                <LinkIcon className={"w-4"} />
                <span> {name}</span>
            </BadgeButton>
        </div>
    ) : (
        ip
    );
}
